import {
  SELECTED_OPTIONS_BY_FIELD_KEY,
  TIntegracaoCampoModel,
  TIntegracaoCampoOpcao,
  TIntegracaoOpcoesSelecionadasPorCampo,
  TIntegracaoRegraExibicao
} from "../../../../@types/IntegracaoCampoModel";

export default function useDisplayRule() {
  const getSelectedOptions = (
    rule: TIntegracaoRegraExibicao,
    values: Record<string, unknown>
  ): TIntegracaoCampoOpcao[] => {
    const selectedOptionsByField = values[SELECTED_OPTIONS_BY_FIELD_KEY] as
      | TIntegracaoOpcoesSelecionadasPorCampo
      | undefined;

    return selectedOptionsByField?.[rule.nomeCampo] ?? [];
  };

  const isFieldVisible = (
    field: TIntegracaoCampoModel,
    values: Record<string, unknown>
  ): boolean => {
    if (field.visivel === false) return false;

    return valueMatchesDisplayRule(field.regraExibicao, values);
  };

  const valuesAreEqual = (a: unknown, b: unknown): boolean => {
    const isBooleanLike = (value: unknown): boolean =>
      typeof value === "boolean" ||
      (typeof value === "string" &&
        ["true", "false", "1", "0", "sim", "nao", "não"].includes(
          value.trim().toLowerCase()
        ));

    if (isBooleanLike(a) || isBooleanLike(b)) {
      const normalizeBoolean = (value: unknown): boolean => {
        if (typeof value === "string") {
          return ["true", "1", "sim"].includes(value.trim().toLowerCase());
        }

        return Boolean(value);
      };

      return normalizeBoolean(a) === normalizeBoolean(b);
    }

    return Object.is(a, b) || String(a) === String(b);
  };

  const ruleIsSatisfied = (
    rule: TIntegracaoRegraExibicao,
    values: Record<string, unknown>
  ): boolean => {
    if (rule.origem === "SelectedOptionAttribute") {
      const selectedOptions = getSelectedOptions(rule, values);
      if (!selectedOptions.length || !rule.atributo) return false;

      const selectedAttributeValues = selectedOptions.map(
        (option) => option.attributes?.[rule.atributo ?? ""] ?? null
      );

      const everyEqual = selectedAttributeValues.every((attributeValue) =>
        rule.valores.some((ruleValue) =>
          valuesAreEqual(ruleValue, attributeValue)
        )
      );
      const anyEqual = selectedAttributeValues.some((attributeValue) =>
        rule.valores.some((ruleValue) =>
          valuesAreEqual(ruleValue, attributeValue)
        )
      );

      if (rule.operador === "NotEquals") {
        return rule.agregacao === "All" ? !anyEqual : !everyEqual;
      }

      return rule.agregacao === "All" ? everyEqual : anyEqual;
    }

    const dependentValue = rule.nomeCampo ? values[rule.nomeCampo] : undefined;
    const expectedValues = rule.valores ?? [];
    const anyEqual = expectedValues.some((value) =>
      valuesAreEqual(value, dependentValue)
    );

    switch (rule.operador) {
      case "NotEquals":
        return !anyEqual;
      case "Equals":
      default:
        return anyEqual;
    }
  };

  const valueMatchesDisplayRule = (
    rule: TIntegracaoRegraExibicao | null,
    values: Record<string, unknown>
  ): boolean => {
    if (!rule?.nomeCampo) return true;
    if (!rule.valores?.length) return true;

    return ruleIsSatisfied(rule, values) === rule.visivelQuandoSatisfaz;
  };

  return {
    isFieldVisible,
    valueMatchesDisplayRule
  };
}
